import { useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useOutputDetail, useOutputEvidence, useRenderedOutputPreview } from "../hooks/useOutputDetail";
import { useRunTrace } from "../hooks/useRunTrace";
import { Badge } from "../components/ui/Badge";
import { Markdown } from "../components/agent/Markdown";
import { ApiError, apiUrl } from "../lib/api";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { OutputView } from "../components/output/OutputView";
import { EvidenceDrawer } from "../components/output/EvidenceDrawer";
import { buildBrandScopedPath, parseBrandIdFromSearch } from "../lib/brand-navigation";
import { resolveWorkspaceReturn } from "../lib/workspace-return";
import { trustGateActionCopy, trustGateLabel, trustGateReasonLabels, trustGateSummary, trustGateVariant } from "../lib/trust-state";
import { workObjectDisplaySpec, type WorkObjectDisplayState } from "../lib/work-object-display";
import { workProductWarningDefinition } from "../../../shared/types";
import type { OutputDocument, TaskRunTraceGraph, WorkProductDetail, WorkProductFamily, WorkProductReadiness, WorkProductRef } from "../../../shared/types";

function humanise(value: string | null | undefined): string {
  return value?.trim()
    ? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Unknown";
}

function readinessLabel(readiness: WorkProductReadiness | undefined): string {
  switch (readiness) {
    case "usable":
      return "Output ready";
    case "usable_with_warnings":
      return "Output warnings";
    case "unlinked":
      return "Output unlinked";
    case "diagnostic_only":
      return "Diagnostic only";
    case "failed":
      return "Failed";
    case "running":
      return "Running";
    default:
      return "Unknown";
  }
}

function readinessVariant(readiness: WorkProductReadiness | undefined): "success" | "warning" | "error" | "default" {
  switch (readiness) {
    case "usable":
      return "success";
    case "usable_with_warnings":
    case "unlinked":
    case "diagnostic_only":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function traceSummary(trace: TaskRunTraceGraph | null | undefined): string {
  if (!trace) return "Trace graph is not loaded yet.";
  return [
    `${trace.work_products.length} output${trace.work_products.length === 1 ? "" : "s"}`,
    `${trace.native_records.filter((record) => record.exists).length}/${trace.native_records.length} native record${trace.native_records.length === 1 ? "" : "s"}`,
    `${trace.supporting_findings.length} supporting finding${trace.supporting_findings.length === 1 ? "" : "s"}`,
    `${trace.source_runs.length} source run${trace.source_runs.length === 1 ? "" : "s"}`,
  ].join(" · ");
}

function warningText(code: string): string {
  const definition = workProductWarningDefinition(code);
  return `${definition.label}: ${definition.description}`;
}

function shortLabel(value: string | null | undefined, prefix: string): string | null {
  return value?.trim() ? `${prefix} ${value.slice(0, 8)}` : null;
}

function storageReference(product: WorkProductRef): string | null {
  if (product.document_key) return `Document ${product.document_key}`;
  if (product.artifact_key) return `Artifact ${product.artifact_key}`;
  if (product.storage_key) return `Storage ${product.storage_key}`;
  return null;
}

function productSubtitle(product: WorkProductRef): string {
  return [
    humanise(product.output_type),
    humanise(product.family),
    shortLabel(product.brief_id, "Brief"),
  ].filter(Boolean).join(" · ");
}

function metadataString(value: Record<string, unknown> | null | undefined, key: string): string | null {
  const candidate = value?.[key];
  return typeof candidate === "string" && candidate.trim().length > 0 ? candidate : null;
}

function metadataRecord(value: Record<string, unknown> | null | undefined, key: string): Record<string, unknown> | null {
  const candidate = value?.[key];
  return candidate && typeof candidate === "object" && !Array.isArray(candidate)
    ? candidate as Record<string, unknown>
    : null;
}

function metadataStringArray(value: Record<string, unknown> | null | undefined, key: string): string[] {
  const candidate = value?.[key];
  return Array.isArray(candidate) ? candidate.filter((item): item is string => typeof item === "string" && item.trim().length > 0) : [];
}

function safeInternalDownloadPath(value: string | null): string | null {
  return value && value.startsWith("/api/agent/artifacts/") ? value : null;
}

type OutputContentPacket = {
  status?: string;
  angle_brief?: string | null;
  review_report?: string | null;
  approval_packet?: string | null;
  source_refs?: Array<{ label?: string | null; url?: string }>;
};

function outputContentPacket(metadata: Record<string, unknown> | null | undefined): OutputContentPacket | null {
  const packet = metadataRecord(metadata, "content_packet");
  return packet ? packet as OutputContentPacket : null;
}

type KeywordMasterSummary = {
  sourceRun: string | null;
  sourceArtifact: string | null;
  revision: string | null;
  findingsCaptured: string | null;
  priorityKeywords: string[];
};

function parseKeywordMasterSummary(content: string): KeywordMasterSummary {
  const sourceRun = content.match(/^- Source run:\s*(.+)$/m)?.[1]?.trim() ?? null;
  const sourceArtifact = content.match(/^- Source artifact:\s*(.+)$/m)?.[1]?.trim() ?? null;
  const revision = content.match(/^- Revision:\s*(.+)$/m)?.[1]?.trim() ?? null;
  const findingsCaptured = content.match(/^- Findings captured:\s*(.+)$/m)?.[1]?.trim() ?? null;

  const keywordSection = content.match(/## Priority Keywords\s+([\s\S]*?)(?:\n## |\s*$)/);
  const priorityKeywords = (keywordSection?.[1] ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("- "))
    .map((line) => line.slice(2).trim())
    .filter(Boolean);

  return {
    sourceRun,
    sourceArtifact,
    revision,
    findingsCaptured,
    priorityKeywords,
  };
}

function contentHeading(product: WorkProductRef): string {
  switch (product.family) {
    case "research_output":
      return "Structured output";
    case "market_research_artifact":
      return "Research report";
    case "current_events_research_artifact":
      return "Current-events research report";
    case "agent_artifact":
      return "Artifact content";
    default:
      return "Content";
  }
}

function outputWorkObjectType(product: WorkProductRef): string {
  if (product.output_type === "published") return "published_handoff";
  if (product.output_type === "voice_review") return "voice_review";
  if (product.output_type === "draft") return "draft";
  if (product.output_type === "brief") return "brief";
  if (product.output_type === "daily_brief") return "daily_brief";
  if (
    product.family === "research_output"
    || product.family === "market_research_artifact"
    || product.family === "current_events_research_artifact"
    || product.output_type.startsWith("research-")
  ) {
    return "market_research_report";
  }
  if (product.family === "campaign_document") return "draft";
  if (product.family === "agent_artifact") return "unknown";
  return product.output_type || "unknown";
}

function outputWorkObjectState(product: WorkProductRef): WorkObjectDisplayState {
  if (product.readiness === "failed" || product.readiness === "unlinked") return "blocked";

  const objectType = outputWorkObjectType(product);
  if (objectType === "voice_review") return "open";
  if (objectType === "market_research_report") return "generated";
  if (objectType === "published_handoff") return "generated";
  return "unknown";
}

function outputPrimaryAction(
  product: WorkProductRef,
  brandId: string | null,
): { label: string; to: string } | null {
  const displaySpec = workObjectDisplaySpec(outputWorkObjectType(product), outputWorkObjectState(product));
  const sourceRunPath = product.source_run_id
    ? buildBrandScopedPath(`/runs/${encodeURIComponent(product.source_run_id)}`, brandId)
    : null;

  if (displaySpec.objectType === "draft" && product.source_run_id) {
    return {
      label: displaySpec.primaryCta.label,
      to: buildBrandScopedPath(`/review/${encodeURIComponent(product.source_run_id)}`, brandId),
    };
  }

  if (displaySpec.objectType === "voice_review" && product.source_run_id) {
    return {
      label: displaySpec.primaryCta.label,
      to: buildBrandScopedPath(`/review/${encodeURIComponent(product.source_run_id)}`, brandId),
    };
  }

  if (sourceRunPath) {
    return {
      label: "Open source run",
      to: sourceRunPath,
    };
  }

  return null;
}

function isHtmlOutput(product: WorkProductRef, metadata: Record<string, unknown> | null | undefined): boolean {
  const contentType = metadataString(metadata, "content_type");
  return product.output_type === "page-html-draft" || contentType === "text/html";
}

function safePreviewDocument(content: string): string {
  const csp = [
    "default-src 'none'",
    "img-src data:",
    "font-src data:",
    "style-src 'unsafe-inline'",
    "connect-src 'none'",
    "media-src 'none'",
    "object-src 'none'",
    "frame-src 'none'",
    "worker-src 'none'",
    "script-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
  ].join("; ");

  if (typeof DOMParser !== "undefined") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const blockedTags = new Set(["script", "iframe", "object", "embed", "audio", "video", "source", "link"]);

    for (const node of Array.from(doc.querySelectorAll("*"))) {
      const tagName = node.tagName.toLowerCase();
      if (blockedTags.has(tagName)) {
        node.remove();
        continue;
      }
      if (tagName === "meta") {
        const httpEquiv = node.getAttribute("http-equiv")?.trim().toLowerCase();
        if (httpEquiv === "content-security-policy" || httpEquiv === "refresh") {
          node.remove();
          continue;
        }
      }
      for (const attr of Array.from(node.attributes)) {
        const name = attr.name.toLowerCase();
        const value = attr.value.trim();
        const lower = value.toLowerCase();
        if (name.startsWith("on")) {
          node.removeAttribute(attr.name);
          continue;
        }
        if (name === "src" || name === "href" || name === "srcset" || name === "poster") {
          if (
            lower.startsWith("http://") ||
            lower.startsWith("https://") ||
            lower.startsWith("//") ||
            lower.startsWith("javascript:")
          ) {
            node.removeAttribute(attr.name);
          }
          continue;
        }
        if (name === "style" && lower.includes("url(")) {
          node.removeAttribute(attr.name);
        }
      }
    }

    const head = doc.head ?? doc.documentElement.insertBefore(doc.createElement("head"), doc.body ?? null);
    const cspMeta = doc.createElement("meta");
    cspMeta.setAttribute("http-equiv", "Content-Security-Policy");
    cspMeta.setAttribute("content", csp);
    head.prepend(cspMeta);
    return `<!doctype html>${doc.documentElement.outerHTML}`;
  }

  const sanitized = content
    .replace(/<meta[^>]+http-equiv\s*=\s*(?:["']?)(?:content-security-policy|refresh)(?:["']?)[^>]*>/gi, "")
    .replace(/<(script|iframe|object|embed|audio|video|source|link)\b[\s\S]*?<\/\1>/gi, "")
    .replace(/<(script|iframe|object|embed|audio|video|source|link)\b[^>]*\/?>/gi, "")
    .replace(/\s(?:src|href|srcset|poster)\s*=\s*(?:["'](?:https?:\/\/|\/\/|javascript:)[^"']*["']|(?:https?:\/\/|\/\/|javascript:)[^\s>]+)/gi, "")
    .replace(/\sstyle\s*=\s*["'][^"']*url\([^"']*\)[^"']*["']/gi, "");

  if (/<head[\s>]/i.test(sanitized)) {
    return sanitized.replace(/<head([^>]*)>/i, `<head$1><meta http-equiv="Content-Security-Policy" content="${csp}">`);
  }

  if (/<html[\s>]/i.test(sanitized)) {
    return sanitized.replace(/<html([^>]*)>/i, `<html$1><head><meta http-equiv="Content-Security-Policy" content="${csp}"></head>`);
  }

  return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${csp}"></head><body>${sanitized}</body></html>`;
}

function renderHtmlContent(
  detail: { content: string | null; metadata: Record<string, unknown> | null },
  product: WorkProductRef,
  renderedHtml: string | null,
  previewState: "loading" | "ready" | "error",
  unresolvedRefs: number,
) {
  const title = product.title?.trim() || "HTML output preview";
  const previewDoc = safePreviewDocument(renderedHtml ?? detail.content ?? "");

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Preview</h3>
            <p className="mt-1 text-sm text-slate-600">
              {previewState === "ready"
                ? "Sandboxed render of the resolved HTML draft for visual review."
                : previewState === "loading"
                  ? "Preparing resolved HTML preview."
                  : "Sandboxed render of the saved HTML draft for visual review."}
            </p>
          </div>
          <Badge variant="default">{previewState === "ready" ? "Resolved HTML preview" : "HTML preview"}</Badge>
        </div>
        {previewState === "error" && (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Resolved asset preview is unavailable. Showing the saved HTML draft instead.
          </p>
        )}
        {unresolvedRefs > 0 && (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {unresolvedRefs} asset reference{unresolvedRefs === 1 ? "" : "s"} could not be resolved for this preview. Missing or deleted assets are omitted from the rendered HTML.
          </p>
        )}
        <iframe
          title={`${title} preview`}
          sandbox=""
          srcDoc={previewDoc}
          className="mt-4 h-[720px] w-full rounded-lg border border-slate-200 bg-white"
        />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-slate-900">HTML source</h3>
          <p className="text-xs text-slate-500">Raw saved markup remains visible for inspection.</p>
        </div>
        <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-lg bg-slate-950 p-4 text-sm text-slate-50">
          {detail.content}
        </pre>
      </div>
    </div>
  );
}

function sourceLabel(product: WorkProductRef): string {
  if (product.source_run_id) return "Action Run";
  switch (product.family) {
    case "brand_document":
      return "Brand document";
    case "research_output":
      return "Research output";
    case "market_research_artifact":
      return "Market research";
    case "current_events_research_artifact":
      return "Current-events research";
    case "agent_artifact":
      return "Agent artifact";
    default:
      return "Native record";
  }
}

function isCampaignMarkdown(metadata: Record<string, unknown> | null, product: WorkProductRef): boolean {
  const surface = metadataString(metadata, "surface") ?? metadataString(metadata, "channel");
  return product.family === "campaign_document" && ["blog", "website"].includes(surface?.toLowerCase() ?? "");
}

function outputReaderTitle(detail: WorkProductDetail, contentMode: "normalized" | "html" | "raw"): string {
  const product = detail.work_product;
  const generatedTitle = product.brief_id
    ? `${product.output_type.replace(/_/g, " ")} ${product.brief_id.slice(0, 8)}`
    : null;
  const dailyTitle = detail.native_record?.type === "daily_brief" && typeof detail.native_record.run_date === "string"
    ? `daily brief ${detail.native_record.run_date}`
    : null;
  const generatedIdentity = product.title === generatedTitle
    || product.title === dailyTitle
    || (!product.brief_id && product.title === "campaign document");
  if (product.family !== "campaign_document" || !generatedIdentity || contentMode === "html") return product.title;
  const document = contentMode === "normalized" ? detail.output_document : null;
  const markdown = document?.format === "markdown"
    ? document.body
    : document?.format === "text" && isCampaignMarkdown(detail.metadata, product)
      ? document.body
      : contentMode === "raw"
        && (isCampaignMarkdown(detail.metadata, product) || metadataString(detail.metadata, "content_type") === "text/markdown")
        ? detail.content
        : null;
  const heading = markdown?.match(/^(?:\uFEFF)?(?:[ \t]*\r?\n)*# ([^\r\n]+)(?:\r?\n|$)/)?.[1]
    ?.replace(/\s*\{#[^}]+\}\s*$/, "").trim();
  // Only a plain authored title can replace the generated identity label.
  // Keep code, HTML and inline Markdown out of the plain-text page title.
  return heading && !/[<>`*_\[\]\\]/.test(heading) ? heading : product.title;
}

function renderContent(detail: { content: string | null; metadata: Record<string, unknown> | null }, product: WorkProductRef) {
  if (!detail.content?.trim()) {
    return <p className="mt-3 text-sm text-slate-600">This output has no text content to display.</p>;
  }

  if (isHtmlOutput(product, detail.metadata)) {
    return null;
  }

  const contentType = metadataString(detail.metadata, "content_type");
  const isMarkdown = product.family === "market_research_artifact"
    || product.family === "current_events_research_artifact"
    // Audit reports are emitted by the trusted brand-audit workflow as Markdown.
    // Keep this narrow: other brand documents can still be arbitrary plain text.
    || (product.family === "brand_document" && product.output_type === "audit-report")
    || product.output_type === "research-keyword-master"
    || product.output_type === "research-strategy-overlay"
    || product.output_type === "research-execution-plan"
    || contentType === "text/markdown"
    || isCampaignMarkdown(detail.metadata, product);
  const isStructured = product.family === "research_output";

  if (isMarkdown) {
    return (
      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
        <Markdown content={detail.content} reading titleAlreadyShown={product.title} />
      </div>
    );
  }

  return (
    <pre aria-label="Output report text" className={`mt-4 max-h-[70vh] overflow-auto overflow-x-auto whitespace-pre-wrap break-words rounded-lg p-4 text-sm ${isStructured ? "bg-slate-100 text-slate-800" : "bg-slate-950 text-slate-50"}`}>
      {detail.content}
    </pre>
  );
}

function renderNormalizedContent(detail: { output_document?: OutputDocument | null; metadata: Record<string, unknown> | null }, product: WorkProductRef) {
  if (!detail.output_document) {
    return <p className="mt-3 text-sm text-slate-600">This output has no normalized document to display.</p>;
  }
  return <OutputView document={detail.output_document} readAsMarkdown={isCampaignMarkdown(detail.metadata, product)} titleAlreadyShown={product.title} />;
}

function renderKeywordMasterSummary(detail: { content: string | null; metadata: Record<string, unknown> | null }, product: WorkProductRef) {
  if (product.output_type !== "research-keyword-master" || !detail.content?.trim()) return null;

  const summary = parseKeywordMasterSummary(detail.content);
  const documentMetadata = metadataRecord(detail.metadata, "document_metadata");
  const packageId = product.package_id ?? metadataString(documentMetadata, "package_id");
  const revisionNumber = product.revision_number
    ?? (() => {
      const raw = documentMetadata?.revision_number;
      return typeof raw === "number" ? raw : null;
    })();
  if (
    !summary.sourceRun
    && !summary.sourceArtifact
    && !summary.findingsCaptured
    && !packageId
    && !revisionNumber
    && summary.priorityKeywords.length === 0
  ) {
    return null;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Keyword master summary</h2>
          <p className="mt-1 text-sm text-slate-600">
            Structured package metadata and extracted priority keywords for this generated research artifact.
          </p>
        </div>
        {revisionNumber ? (
          <Badge variant="default">Revision {revisionNumber}</Badge>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Package</p>
          <p className="mt-2 break-all text-sm text-slate-800">{packageId ?? "—"}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source run</p>
          <p className="mt-2 break-all text-sm text-slate-800">{summary.sourceRun ?? product.source_run_id ?? "—"}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source artifact</p>
          <p className="mt-2 break-all text-sm text-slate-800">{summary.sourceArtifact ?? "—"}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Findings captured</p>
          <p className="mt-2 text-sm text-slate-800">{summary.findingsCaptured ?? "—"}</p>
        </div>
      </div>

      {summary.priorityKeywords.length > 0 && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Priority keywords</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {summary.priorityKeywords.map((keyword) => (
              <span
                key={keyword}
                className="rounded-full border border-brand-100 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function lineValueFromDoc(content: string, label: string): string | null {
  const prefix = `- ${label}:`;
  for (const rawLine of content.split("\n")) {
    if (!rawLine.startsWith(prefix)) continue;
    return rawLine.slice(prefix.length).trim() || null;
  }
  return null;
}

function parsePackageDocHeader(content: string): {
  sourceRun: string | null;
  sourceArtifact: string | null;
  keywordMasterDoc: string | null;
  strategyOverlayDoc: string | null;
  revision: string | null;
} {
  return {
    sourceRun: lineValueFromDoc(content, "Source run"),
    sourceArtifact: lineValueFromDoc(content, "Source artifact"),
    keywordMasterDoc: lineValueFromDoc(content, "Keyword master document"),
    strategyOverlayDoc: lineValueFromDoc(content, "Strategy overlay document"),
    revision: lineValueFromDoc(content, "Revision"),
  };
}

function renderPackageSynthesisSummary(detail: { content: string | null; metadata: Record<string, unknown> | null }, product: WorkProductRef) {
  if (product.output_type !== "research-strategy-overlay" && product.output_type !== "research-execution-plan") {
    return null;
  }
  if (!detail.content?.trim()) return null;

  const parsed = parsePackageDocHeader(detail.content);
  const documentMetadata = metadataRecord(detail.metadata, "document_metadata");
  const packageId = product.package_id ?? metadataString(documentMetadata, "package_id");
  const revisionNumber = product.revision_number
    ?? (() => {
      const raw = documentMetadata?.revision_number;
      return typeof raw === "number" ? raw : null;
    })();

  const title = product.output_type === "research-strategy-overlay" ? "Strategy overlay summary" : "Execution plan summary";

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-600">
            Package-linked synthesis artifact with explicit upstream document lineage.
          </p>
        </div>
        {revisionNumber ? (
          <Badge variant="default">Revision {revisionNumber}</Badge>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Package</p>
          <p className="mt-2 break-all text-sm text-slate-800">{packageId ?? "—"}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source run</p>
          <p className="mt-2 break-all text-sm text-slate-800">{parsed.sourceRun ?? product.source_run_id ?? "—"}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source artifact</p>
          <p className="mt-2 break-all text-sm text-slate-800">{parsed.sourceArtifact ?? "—"}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Keyword master</p>
          <p className="mt-2 break-all text-sm text-slate-800">{parsed.keywordMasterDoc ?? "—"}</p>
        </div>
        {product.output_type === "research-execution-plan" ? (
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Strategy overlay</p>
            <p className="mt-2 break-all text-sm text-slate-800">{parsed.strategyOverlayDoc ?? "—"}</p>
          </div>
        ) : null}
        {parsed.revision && !revisionNumber ? (
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Revision (document)</p>
            <p className="mt-2 text-sm text-slate-800">{parsed.revision}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function traceSubtitle({
  hasTraceSource,
  trace,
  traceError,
  traceLoading,
}: {
  hasTraceSource: boolean;
  trace: TaskRunTraceGraph | null;
  traceError: Error | null;
  traceLoading: boolean;
}): string {
  if (traceLoading) return "Loading output trace...";
  if (traceError) return "Could not load trace graph.";
  if (!hasTraceSource) return "No source run is linked for this output yet.";
  return traceSummary(trace);
}

function outputErrorState(error: unknown): { title: string; message: string } {
  if (error instanceof ApiError) {
    if (error.code === "UNSUPPORTED_OUTPUT_FAMILY") {
      return {
        title: "Output family not supported yet",
        message: "This output route exists, but this output family does not have a detail view yet.",
      };
    }
    if (error.code === "NOT_FOUND") {
      return {
        title: "Output not found",
        message: "This output could not be resolved for the current brand. It may be missing, unlinked, or no longer available.",
      };
    }
    if (error.code === "FORBIDDEN") {
      return {
        title: "Output access denied",
        message: "You do not have permission to open this output for the active brand.",
      };
    }
  }
  return {
    title: "Output unavailable",
    message: error instanceof Error ? error.message : "Output not found",
  };
}

export function OutputDetail() {
  const params = useParams();
  const location = useLocation();
  const family = params.family as WorkProductFamily | undefined;
  const key = params.key;
  const brandId = parseBrandIdFromSearch(location.search);
  const workspaceReturn = resolveWorkspaceReturn(location);
  const { data, isLoading, error } = useOutputDetail(family, key);
  const detailData = data?.data;
  const outputProduct = detailData?.work_product;
  const evidenceQuery = useOutputEvidence(family, key);
  const useNormalizedDocument = Boolean(
    detailData?.output_document
    && outputProduct?.family === "campaign_document",
  );
  const htmlOutput = detailData && outputProduct ? isHtmlOutput(outputProduct, detailData.metadata) : false;
  const contentMode = useNormalizedDocument ? "normalized" : htmlOutput ? "html" : "raw";
  const readerTitle = detailData ? outputReaderTitle(detailData, contentMode) : null;
  const renderedPreview = useRenderedOutputPreview(
    htmlOutput ? outputProduct?.brand_id : undefined,
    htmlOutput ? detailData?.content : null,
    "html",
    Boolean(htmlOutput),
  );
  const { data: traceRes, error: traceError, isLoading: traceLoading } = useRunTrace(outputProduct?.source_run_id ?? undefined);

  useEffect(() => {
    if (readerTitle?.trim()) {
      document.title = `${readerTitle} — HelloConvo`;
      return () => {
        document.title = "HelloConvo";
      };
    }

    document.title = "Output — HelloConvo";
    return () => {
      document.title = "HelloConvo";
    };
  }, [readerTitle]);

  if (isLoading) {
    return (
      <div className="job-result-frame mx-auto flex min-h-[60vh] w-full max-w-7xl items-center justify-center px-4 py-4 md:px-6" data-testid="job-result-frame">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !data?.data) {
    const errorState = outputErrorState(error);
    return (
      <div className="job-result-frame mx-auto w-full max-w-7xl space-y-4 px-4 py-4 md:px-6" data-testid="job-result-frame">
        <WorkspaceHeader
          eyebrow="Operator Workspace · Output"
          title="Output unavailable"
          description={errorState.message}
          breadcrumbs={[
            { label: "Workspace", href: "/home" },
            { label: workspaceReturn.label, href: workspaceReturn.href },
            { label: "Output" },
          ]}
        />
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
          <p className="font-medium">{errorState.title}</p>
          <p className="mt-1">{errorState.message}</p>
        </div>
      </div>
    );
  }

  const detail = data.data;
  const resolvedPreviewHtml = renderedPreview.data?.data?.resolved ?? null;
  const unresolvedPreviewRefs = renderedPreview.data?.data?.refs.filter((ref) => ref.unresolved || !ref.url).length ?? 0;
  const htmlPreviewState: "loading" | "ready" | "error" =
    htmlOutput
      ? renderedPreview.isLoading
        ? "loading"
        : renderedPreview.isError
          ? "error"
          : "ready"
      : "ready";
  const product = { ...detail.work_product, title: readerTitle ?? detail.work_product.title };
  const trace = traceRes?.data ?? null;
  const trustGate = detail.trust_gate ?? trace?.trust_gate ?? null;
  const hasTraceSource = Boolean(product?.source_run_id);
  const downloadPath = safeInternalDownloadPath(metadataString(detail.metadata, "download_path"));
  const contentPacket = product.family === "campaign_document" ? outputContentPacket(detail.metadata) : null;
  const displaySpec = workObjectDisplaySpec(outputWorkObjectType(product), outputWorkObjectState(product));
  const primaryAction = outputPrimaryAction(product, brandId);

  return (
    <div className="job-result-frame mx-auto w-full max-w-7xl space-y-6 px-4 py-4 md:px-6" data-testid="job-result-frame">
      <WorkspaceHeader
        eyebrow="Operator Workspace · Output"
        title={product.title}
        description={productSubtitle(product)}
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: workspaceReturn.label, href: workspaceReturn.href },
          { label: "Output" },
        ]}
        badges={(
          <>
            <Badge>{displaySpec.typeLabel}</Badge>
            <Badge variant={readinessVariant(product.readiness)}>
              {readinessLabel(product.readiness)}
            </Badge>
          </>
        )}
        metadata={(
          <>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {humanise(product.output_type)}
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {product.source_run_id ? "Action Result" : sourceLabel(product)}
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Primary action {primaryAction?.label ?? displaySpec.primaryCta.label}
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {new Date(product.created_at).toLocaleString()}
            </span>
          </>
        )}
        actions={(
          <>
            <Link to={workspaceReturn.href} className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Back to {workspaceReturn.label}
            </Link>
            {primaryAction
              ? (
            <Link to={primaryAction.to} className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {primaryAction.label}
            </Link>
              )
              : null}
          </>
        )}
      />

      <div className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">{contentHeading(product)}</h2>
        {contentMode === "normalized"
          ? renderNormalizedContent(detail, product)
          : contentMode === "html"
          ? renderHtmlContent(detail, product, resolvedPreviewHtml, htmlPreviewState, unresolvedPreviewRefs)
          : renderContent(detail, product)}
      </div>

      {renderKeywordMasterSummary(detail, product)}

      {renderPackageSynthesisSummary(detail, product)}

      {product.family === "agent_artifact" && !product.brand_id && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">This artifact is still user-scoped.</p>
          <p className="mt-1">
            It can be opened directly, but it does not appear in any brand Outputs index until it is linked to a brand-scoped session or source run.
          </p>
        </div>
      )}

      {contentPacket && (
        <div className="rounded-[20px] border border-blue-200 bg-blue-50 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-blue-950">Content packet context</h2>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {contentPacket.status ? <Badge variant="info">{contentPacket.status.replace(/_/g, " ")}</Badge> : null}
            {Array.isArray(contentPacket.source_refs) && contentPacket.source_refs.length > 0 ? (
              <span className="text-xs text-slate-600">{contentPacket.source_refs.length} source ref{contentPacket.source_refs.length === 1 ? "" : "s"}</span>
            ) : null}
          </div>
          {contentPacket.angle_brief ? <p className="mt-3 text-sm text-slate-800"><span className="font-semibold">Angle brief:</span> {contentPacket.angle_brief}</p> : null}
          {contentPacket.review_report ? <p className="mt-2 text-sm text-slate-800"><span className="font-semibold">Review report:</span> {contentPacket.review_report}</p> : null}
          {contentPacket.approval_packet ? <p className="mt-2 text-sm text-slate-800"><span className="font-semibold">Approval packet:</span> {contentPacket.approval_packet}</p> : null}
          {Array.isArray(contentPacket.source_refs) && contentPacket.source_refs.length > 0 ? (
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {contentPacket.source_refs.map((ref) => (
                <li key={ref.url ?? String(ref.label)}>{ref.label ? `${ref.label}: ` : ""}{ref.url ?? ""}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      {trustGate && (
        <div className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Trust state</h2>
              <p className="mt-1 text-sm text-slate-600">{trustGateSummary(trustGate)}</p>
            </div>
            <Badge variant={trustGateVariant(trustGate)}>{trustGateLabel(trustGate)}</Badge>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {trustGateReasonLabels(trustGate).map((reason) => (
              <span key={reason} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                {reason}
              </span>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-700">{trustGateActionCopy(trustGate)}</p>
        </div>
      )}

      <details className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm" open>
        <summary className="cursor-pointer text-sm font-semibold text-slate-900">Supporting context</summary>
        <div className="mt-4 space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Brand</p>
              <p className="mt-2 break-all text-sm text-slate-800">{product.brand_id || "User-scoped artifact"}</p>
            </div>
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Campaign / Brief</p>
              <p className="mt-2 break-all text-sm text-slate-800">
                {[product.campaign_id, product.brief_id].filter(Boolean).join(" · ") || "—"}
              </p>
            </div>
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Storage reference</p>
              <p className="mt-2 break-all text-sm text-slate-800">{storageReference(product) ?? "—"}</p>
            </div>
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Created</p>
              <p className="mt-2 text-sm text-slate-800">{new Date(product.created_at).toLocaleString()}</p>
            </div>
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source</p>
              <p className="mt-2 text-sm text-slate-800">
                {product.source_run_id ? "Action Result" : sourceLabel(product)}
              </p>
            </div>
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Output type</p>
              <p className="mt-2 text-sm text-slate-800">{humanise(product.output_type)}</p>
            </div>
          </div>

          {(downloadPath || metadataString(detail.metadata, "workflow_run_id") || metadataString(detail.metadata, "run_id") || metadataString(detail.metadata, "session_id") || metadataStringArray(detail.metadata, "keyword_seeds").length > 0) && (
            <div className="grid gap-4 md:grid-cols-3">
              {metadataString(detail.metadata, "run_id") && (
                <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Run id</p>
                  <p className="mt-2 break-all text-sm text-slate-800">{metadataString(detail.metadata, "run_id")}</p>
                </div>
              )}
              {metadataString(detail.metadata, "workflow_run_id") && (
                <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Workflow run</p>
                  <p className="mt-2 break-all text-sm text-slate-800">{metadataString(detail.metadata, "workflow_run_id")}</p>
                </div>
              )}
              {metadataString(detail.metadata, "session_id") && (
                <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Session</p>
                  <p className="mt-2 break-all text-sm text-slate-800">{metadataString(detail.metadata, "session_id")}</p>
                </div>
              )}
              {metadataStringArray(detail.metadata, "keyword_seeds").length > 0 && (
                <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm md:col-span-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Keyword seeds</p>
                  <p className="mt-2 text-sm text-slate-800">{metadataStringArray(detail.metadata, "keyword_seeds").join(" · ")}</p>
                </div>
              )}
              {downloadPath && (
                <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Actions</p>
                  <a
                    href={apiUrl(downloadPath)}
                    className="mt-2 inline-flex text-sm font-medium text-brand-700 hover:underline"
                  >
                    Download artifact
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Trace graph</h2>
                <p className="mt-1 text-sm text-slate-600">
                  {traceSubtitle({ hasTraceSource, trace, traceError: traceError instanceof Error ? traceError : null, traceLoading })}
                </p>
              </div>
              {product.source_run_id && (
                <Link to={buildBrandScopedPath(`/runs/${encodeURIComponent(product.source_run_id)}`, brandId)} className="text-sm font-medium text-brand-700 hover:underline">
                  Open source run
                </Link>
              )}
            </div>
            {traceError && (
              <p className="mt-3 text-sm text-amber-700" role="alert">
                Trace graph is unavailable: {traceError instanceof Error ? traceError.message : "unknown error"}
              </p>
            )}
            {trace && (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Native records</p>
                  <div className="mt-2 space-y-2">
                    {trace.native_records.map((record) => (
                      <div key={`${record.family}:${record.key}`} className="text-sm text-slate-700">
                        <p className="font-medium text-slate-900">{record.title ?? record.key}</p>
                        <p className="break-all text-xs text-slate-500">
                          {record.exists ? "Linked" : "Missing"} · {record.storage_collection ?? "unknown collection"} · {record.key}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Evidence</p>
                  <p className="mt-2 text-sm text-slate-700">
                    {trace.supporting_findings.length} finding{trace.supporting_findings.length === 1 ? "" : "s"} {trace.supporting_findings.length === 1 ? "supports" : "support"} this output.
                  </p>
                  {(trace.warnings.length > 0) && (
                    <p className="mt-2 text-xs text-amber-700">Trace warnings: {trace.warnings.map(warningText).join("; ")}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {detail.metadata && Object.keys(detail.metadata).length > 0 && (
            <details className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
              <summary className="cursor-pointer text-sm font-semibold text-slate-900">Metadata</summary>
              <pre className="mt-4 overflow-auto rounded-lg bg-slate-100 p-4 text-xs text-slate-700">
                {JSON.stringify(detail.metadata, null, 2)}
              </pre>
            </details>
          )}

          <EvidenceDrawer
            grounding={evidenceQuery.data?.data.grounding ?? detail.grounding ?? null}
            evidence={evidenceQuery.data?.data.evidence ?? []}
            isLoading={evidenceQuery.isLoading}
            errorMessage={evidenceQuery.error instanceof Error ? evidenceQuery.error.message : null}
          />
        </div>
      </details>
    </div>
  );
}
